import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { MessageCircle, X, Send, Phone, Image as ImageIcon } from 'lucide-react';

export default function HelplineWidget() {
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `sess-${Date.now()}`);
  const [faqSearch, setFaqSearch] = useState('');
  const chatEndRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/helpline/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!settings) return null;

  const mode = settings.mode;

  // WhatsApp/Telegram mode
  if (mode === 'whatsapp') {
    return (
      <div className="helpline-fab-container">
        {open && (
          <div className="fixed bottom-36 right-4 z-[1000] glass-card rounded-2xl p-4 w-72"
            style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-bold text-sm" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>Helpline</p>
              <button onClick={() => setOpen(false)} style={{ color: 'var(--neon-text-muted)' }}><X size={16} /></button>
            </div>
            <div className="space-y-2">
              {settings.whatsapp_number && (
                <a href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer"
                  data-testid="whatsapp-link"
                  className="flex items-center gap-3 p-3 rounded-xl w-full transition-all hover:scale-[1.02]"
                  style={{ background: 'rgba(37,211,102,0.15)', border: '1px solid rgba(37,211,102,0.3)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#25D366' }}>
                    <Phone size={18} color="#fff" />
                  </div>
                  <div>
                    <p className="font-bold text-sm" style={{ color: '#25D366' }}>WhatsApp</p>
                    <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Chat with us</p>
                  </div>
                </a>
              )}
              {settings.telegram_link && (
                <a href={settings.telegram_link} target="_blank" rel="noreferrer"
                  data-testid="telegram-link"
                  className="flex items-center gap-3 p-3 rounded-xl w-full transition-all hover:scale-[1.02]"
                  style={{ background: 'rgba(0,136,204,0.15)', border: '1px solid rgba(0,136,204,0.3)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#0088CC' }}>
                    <Send size={18} color="#fff" />
                  </div>
                  <div>
                    <p className="font-bold text-sm" style={{ color: '#0088CC' }}>Telegram</p>
                    <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Message us</p>
                  </div>
                </a>
              )}
              {!settings.whatsapp_number && !settings.telegram_link && (
                <p className="text-xs text-center py-4" style={{ color: 'var(--neon-text-muted)' }}>Contact info not set by admin yet.</p>
              )}
            </div>
          </div>
        )}
        <button data-testid="helpline-fab" className="helpline-fab" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <MessageCircle size={22} />}
        </button>
      </div>
    );
  }

  // FAQ mode
  if (mode === 'faq') {
    const faqs = settings.faq_items || [];
    const filtered = faqSearch ? faqs.filter(f => f.question?.toLowerCase().includes(faqSearch.toLowerCase()) || f.answer?.toLowerCase().includes(faqSearch.toLowerCase())) : faqs;
    return (
      <div>
        {open && (
          <div className="fixed bottom-36 right-4 z-[1000] glass-card rounded-2xl w-80 max-h-[60vh] flex flex-col"
            style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center justify-between p-4 pb-2">
              <p className="font-bold text-sm" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>FAQ - Help</p>
              <button onClick={() => setOpen(false)} style={{ color: 'var(--neon-text-muted)' }}><X size={16} /></button>
            </div>
            <div className="px-4 pb-2">
              <input data-testid="faq-search" className="neon-input w-full text-sm p-2 rounded-lg" placeholder="Search..."
                style={{ background: 'var(--neon-elevated)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--neon-text)' }}
                value={faqSearch} onChange={e => setFaqSearch(e.target.value)} />
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
              {filtered.length === 0 && <p className="text-xs text-center py-4" style={{ color: 'var(--neon-text-muted)' }}>No FAQ found</p>}
              {filtered.map((faq, i) => (
                <details key={i} data-testid={`faq-item-${i}`} className="rounded-lg" style={{ background: 'var(--neon-elevated)' }}>
                  <summary className="p-3 text-sm font-bold cursor-pointer" style={{ color: 'var(--neon-text)' }}>{faq.question}</summary>
                  <p className="px-3 pb-3 text-xs" style={{ color: 'var(--neon-text-secondary)' }}>{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        )}
        <button data-testid="helpline-fab" className="helpline-fab" onClick={() => setOpen(!open)}>
          {open ? <X size={22} /> : <MessageCircle size={22} />}
        </button>
      </div>
    );
  }

  // AI Chatbot mode
  const sendMessage = async (text, imageBase64) => {
    if (!text && !imageBase64) return;
    const userMsg = { role: 'user', text, hasImage: !!imageBase64 };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const body = { message: text || 'Analyze this image', session_id: sessionId };
      if (imageBase64) body.image_base64 = imageBase64;
      const res = await api.post('/chatbot/message', body);
      setMessages(prev => [...prev, { role: 'bot', text: res.data.reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'bot', text: err.response?.data?.detail || 'Error. Try again.' }]);
    }
    setLoading(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      sendMessage(input || 'Please check this screenshot', base64);
    };
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div>
      {open && (
        <div className="fixed bottom-36 right-4 z-[1000] glass-card rounded-2xl w-80 flex flex-col"
          style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)', height: '400px' }}>
          <div className="flex items-center justify-between p-4 pb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <p className="font-bold text-sm" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
              <span className="brand-glow">Mukamari</span> Help
            </p>
            <button onClick={() => setOpen(false)} style={{ color: 'var(--neon-text-muted)' }}><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <MessageCircle size={28} className="mx-auto mb-2" style={{ color: 'var(--neon-primary)' }} />
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Ask anything about Mukamari!</p>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Hindi / English</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[85%] p-2.5 rounded-xl text-xs"
                  style={{
                    background: msg.role === 'user' ? 'rgba(5,217,232,0.15)' : 'var(--neon-elevated)',
                    color: 'var(--neon-text)',
                    borderBottomRightRadius: msg.role === 'user' ? '4px' : '12px',
                    borderBottomLeftRadius: msg.role === 'user' ? '12px' : '4px'
                  }}>
                  {msg.hasImage && <p className="text-xs mb-1" style={{ color: 'var(--neon-accent)' }}>[Screenshot attached]</p>}
                  <p style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="p-2.5 rounded-xl text-xs animate-pulse" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-text-muted)' }}>
                  Typing...
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="p-3 flex gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
            <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={handleImageUpload} />
            <button data-testid="chat-image-btn" onClick={() => fileRef.current?.click()}
              className="p-2 rounded-lg" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-accent)' }}>
              <ImageIcon size={16} />
            </button>
            <input data-testid="chat-input" className="flex-1 text-sm p-2 rounded-lg"
              style={{ background: 'var(--neon-elevated)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--neon-text)' }}
              placeholder="Type message..."
              value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }} />
            <button data-testid="chat-send-btn" onClick={() => sendMessage(input)} disabled={loading || !input.trim()}
              className="p-2 rounded-lg disabled:opacity-30" style={{ background: 'var(--neon-primary)', color: '#000' }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
      <button data-testid="helpline-fab" className="helpline-fab" onClick={() => setOpen(!open)}>
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </div>
  );
}
